/* eslint-disable react/require-default-props */
import React from "react";
import { Alert, Button, Form, Input, Select } from "antd";
import tinycolor from "tinycolor2";
import { KeyboardArrowDownOutlined } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { isPossiblePhoneNumber } from "libphonenumber-js";
import { getPublicEmailDomains, isPublicEmailDomain } from "./publicEmailDomains";
import PhoneNumberInput, { isPlausiblePhoneNumber } from "./PhoneNumberInput";
import { CustomTitle, FormBottomWrap, FormItemWrap, FormWrap, Section } from "./StyledComponent";

const formData = [
  {
    label: "Company *",
    placeholder: "input content",
  },
  {
    label: "Full name *",
    placeholder: "input content",
  },
  {
    label: "Email Address *",
    placeholder: "input content",
  },
];

// program to convert first letter of a string to uppercase
function capitalizeFirstLetter(str: string) {
  // converting first letter to uppercase
  const capitalized = str.charAt(0).toUpperCase() + str.slice(1);

  return capitalized;
}

interface IRegistrationFormTypes {
  buttonColor?: "default" | "primary" | "danger" | undefined;
  preview?: boolean;
  handleSuccessMessage?: any;
  registrationData?: any;
  successMessage?: boolean;
  onFinish: any;
  pageType?: string;
  isLoading?: boolean;
}

// Preview never submits, so it has no backend response to surface. Match the
// backend's copy exactly (registrations/base/serializers.py).
const PUBLIC_EMAIL_ERROR = "Please register with your work email address.";
const DOMAIN_LOOKUP_ERROR = "Unable to verify your email domain right now. Please try again.";

const publicEmailRule = {
  async validator(_: unknown, value: string) {
    if (!value) return;
    let isPublic: boolean;
    try {
      isPublic = await isPublicEmailDomain(value);
    } catch {
      // Fail closed: can't verify the domain, so don't let it through.
      throw new Error(DOMAIN_LOOKUP_ERROR);
    }
    if (isPublic) {
      throw new Error(PUBLIC_EMAIL_ERROR);
    }
  },
};

const PHONE_NUMBER_ERROR = "Please enter a valid phone number";

// Replaces the `type` rule for phone fields: "phone_number" is not an async-validator type, so
// that rule threw and let any value through. With `validate_phone_number`, the length must also
// be possible for the number's country.
const phoneNumberRule = (item: any) => ({
  async validator(_: unknown, value: string) {
    if (!value) return;
    const isValid =
      isPlausiblePhoneNumber(value) &&
      (!item?.validate_phone_number || isPossiblePhoneNumber(value));
    if (!isValid) {
      throw new Error(PHONE_NUMBER_ERROR);
    }
  },
});

const isPhoneNumber = (item: any) => item?.data_type === "phone_number";

const blocksPublicEmails = (item: any) =>
  item?.data_type === "email" && !!item?.block_public_email_domains;

// A warm domain list resolves in a microtask, so hold the spinner back briefly rather than
// flashing it on every submit. antd only treats the button as loading once the delay is up,
// which also keeps a fast click from being preventDefaulted.
const SUBMIT_SPINNER_DELAY_MS = 150;

const defaultPropsTypes = {
  buttonColor: "green",
};

function RegistrationForm({
  buttonColor,
  preview,
  handleSuccessMessage,
  registrationData,
  successMessage,
  onFinish,
  pageType,
  isLoading,
}: IRegistrationFormTypes) {
  const [form] = Form.useForm();
  const [formError, setFormError] = React.useState("");
  // The domain lookup is async, so without this a submit landing mid-fetch leaves the
  // button inert for as long as the sources take to answer. Driven by the submit click
  // rather than by field `validating` meta: antd's Button preventDefaults clicks while
  // loading, so reflecting per-keystroke validation here would swallow the submit that
  // lands right after the last character — on the live pages too.
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const pageFields = registrationData?.registration_page_fields;
  // The single switch for everything in this file that is preview-only. False on the live
  // buyer pages, which never pass the prop, so none of it can reach them.
  const checksPublicEmails = (pageFields || []).some(blocksPublicEmails);

  React.useEffect(() => {
    if (checksPublicEmails) {
      // Warm the cache while the user reads the form; failures surface on validation.
      getPublicEmailDomains().catch(() => undefined);
    }
  }, [checksPublicEmails]);

  const handleOptions = (options: any) => {
    const entries = Object.entries(options);
    const option = entries?.map((item: any) => ({
      label: item?.[1],
      value: item?.[1],
    }));
    return option;
  };

  const renderFieldInput = (item: any) => {
    if (item?.data_type === "dropdown") {
      return (
        <Select
          allowClear
          showSearch
          dropdownMatchSelectWidth
          placeholder={`Please enter your ${item?.title}`}
          options={handleOptions(item?.options || {})}
          suffixIcon={
            <KeyboardArrowDownOutlined sx={{ height: "20px", width: "20px", color: "#AEB6BE" }} />
          }
        />
      );
    }
    if (isPhoneNumber(item)) {
      return <PhoneNumberInput />;
    }
    return (
      <Input
        placeholder={
          blocksPublicEmails(item)
            ? "Enter your work email address"
            : `Please enter your ${item?.title}`
        }
        name={item?.title}
        // type={item?.data_type}
      />
    );
  };

  const messages: any = {
    url: "Please enter a valid url",
    email: " Please enter a valid email",
    text: "",
    dropdown: "",
  };

  if (registrationData?.gcp_user_details) {
    form.setFieldsValue({
      "Full Name": registrationData?.gcp_user_details?.name,
      "Email Address": registrationData?.gcp_user_details?.email,
    });
  }

  const handleFinish = async (values: any) => {
    // Validation is done by the time antd calls this; `isLoading` owns the submit phase.
    setIsSubmitting(false);
    const error = await onFinish(values);
    if (!error) {
      setFormError("");
      return;
    }
    const apiErrors = error?.data?.errors;
    let mappedCount = 0;
    if (Array.isArray(apiErrors)) {
      const fields = registrationData?.registration_page_fields || [];
      const formErrors = apiErrors
        .map((e: any) => {
          const idx = Number(String(e?.field).split(".")[0]);
          const title = fields[idx]?.title;
          return title ? { name: title, errors: [e?.message] } : null;
        })
        .filter(Boolean) as { name: string; errors: string[] }[];
      if (formErrors.length) {
        form.setFields(formErrors);
        mappedCount = formErrors.length;
      }
    }
    // Any failure not mapped to a specific field (500, network/timeout,
    // auth, unmapped index) still needs visible feedback.
    setFormError(
      mappedCount ? "" : error?.data?.message || "Something went wrong. Please try again.",
    );
  };

  return (
    <FormWrap
      gap='32'
      labelFontSize='14'
      labelLineHeight='22'
      padding='48px 68px 88px'
      className={`${pageType === "clean" && "clean-formwrap"}`}
    >
      <Form
        layout='vertical'
        form={form}
        onFinish={handleFinish}
        validateTrigger='onChange'
        scrollToFirstError
        className='registration-main-form'
        onFinishFailed={() => setIsSubmitting(false)}
      >
        <CustomTitle
          color='#000000'
          fontSize={24}
          lineheight={32}
          fontWeight={600}
          fontFamily='nunito-sans'
          className='form-header'
        >
          Complete your registration
        </CustomTitle>
        {formError && (
          <Alert type='error' showIcon message={formError} style={{ marginBottom: 16 }} />
        )}
        <Section>
          <FormItemWrap gap='24' className={`${pageType === "clean" && "clean-section"}`}>
            {registrationData?.registration_page_fields?.length
              ? registrationData?.registration_page_fields?.map((item: any) => (
                  <Form.Item
                    label={item?.title}
                    name={item?.title}
                    rules={[
                      {
                        required: item.required,
                        message: `${capitalizeFirstLetter(
                          item?.title || "This field",
                        )} is required`,
                      },
                      isPhoneNumber(item)
                        ? phoneNumberRule(item)
                        : {
                            message: messages[item?.data_type] || "Please enter a valid input",
                            type: item.data_type,
                          },
                      ...(blocksPublicEmails(item) ? [publicEmailRule] : []),
                    ]}
                  >
                    {renderFieldInput(item)}
                  </Form.Item>
                ))
              : formData.map((item) => (
                  <Form.Item label={item.label}>
                    <Input placeholder={item.placeholder} />
                  </Form.Item>
                ))}
          </FormItemWrap>
        </Section>
        <FormBottomWrap>
          <Button
            htmlType='submit'
            onClick={() => checksPublicEmails && setIsSubmitting(true)}
            color={buttonColor}
            style={{
              backgroundColor: buttonColor || "#0D1A5B",
              fontSize: "16px",
              fontWeight: "600",
              height: "48px",
              color: `${tinycolor(buttonColor).isLight() ? "#000000" : "#ffffff"}`,
            }}
            loading={isLoading || (isSubmitting && { delay: SUBMIT_SPINNER_DELAY_MS })}
          >
            Complete registration
          </Button>
          <CustomTitle
            fontSize={12}
            fontWeight={400}
            lineheight={20}
            color='#212529'
            style={{
              textAlign: "center",
            }}
            className='form-bottom'
          >
            By submitting this form, I agree that my information will be shared with{" "}
            {registrationData?.title || registrationData?.domain} in accordance with the{" "}
            {registrationData?.privacy_policy_url ? (
              <Link to={registrationData?.privacy_policy_url} target='_blank'>
                Privacy Policy
              </Link>
            ) : (
              <span className='text-style'>Privacy Policy</span>
            )}{" "}
            and{" "}
            {registrationData?.terms_of_use_url ? (
              <Link to={registrationData?.terms_of_use_url} target='_blank'>
                Terms of Use.
              </Link>
            ) : (
              <span className='text-style'>Terms of Use.</span>
            )}
            {/* <span className='text-style'>Privacy Policy</span> and{" "}
        <span className='text-style'>Terms of Use.</span> */}
          </CustomTitle>
        </FormBottomWrap>
      </Form>
    </FormWrap>
  );
}

RegistrationForm.defaultProps = defaultPropsTypes;

export default RegistrationForm;
